import { IssueItem, IssuePerDayCount } from '../models/issueItem';
import sampleIssues from '../data/sample_issues.json'; // Import the JSON data
import samplePerDateCounts from '../data/sample_per_date_count.json';

class IssueService {

  /**
   * Fetches issues from a local JSON file.
   *
   * @returns {Promise<IssueItem[]>} A promise that resolves to an array of IssueItem objects.
   */
  async getIssues(): Promise<IssueItem[]> {

    console.log( "getIssues(): Load from the sample issues.")
    try {
      // Simulate an API call delay (optional)
      await new Promise((resolve) => setTimeout(resolve, 500)); // 500ms delay

      // Load data from the imported JSON file
      const issues: IssueItem[] = sampleIssues.issues;

      // Basic validation to ensure data is in the expected format
      if (!Array.isArray(issues)) {
        throw new Error('Invalid data format: Expected an array of issues.');
      }

      // Add any necessary data transformations here if needed
      // Example:
      // const transformedIssues = issues.map(issue => ({
      //   ...issue,
      //   createdAt: new Date(issue.createdAt),
      // }));

      return issues;
    } catch (error) {
      console.error('Error fetching issues:', error);
      throw error; // Re-throw the error to be handled by the caller
    }
  }

  /**
   * Fetches issues from supplied JSON sring document
   *
   * @returns {IssueItem[]} an array of IssueItem objects.
   */
  getIssuesFromJSON(jsonString: string, linkPrefix: string = ""): IssueItem[] {

    console.log( "getIssuesFromJSON(): Load from the supplied Issues in JSON format.")
    console.log( jsonString);
    const jsonData = JSON.parse(jsonString);
    console.log( jsonData);
    const issuesArray = Array.isArray(jsonData) ? jsonData : jsonData.issues;

    if (!issuesArray) {
      throw new Error('JSON data provided does not have suitable structure for IssueItem array.');
    }
    return issuesArray.map((item: any, index: number) => ({
      id: item.id,
      index: index, // startId + index,
      title: item.title, // item["exception_message"],
      link: item.link || `${linkPrefix}/${index + 1}`,
      count: item.count,
      start_at: item.start_at,
      end_at: item.end_at,

    //   description: item.description,
    //   stacktrace: item.stacktrace,
    //   possible_solution: item.possible_solution,
    //   status: item.status,
    //   createdAt: item.createdAt,
    //   updatedAt: item.updatedAt,
    }));

  };

  /**
   * Fetches issues from supplied CSV sring document
   *
   * @returns {IssueItem[]} an array of IssueItem objects.
   */
  getIssuesFromCSV( csvString: string, linkPrefix: string = ""): IssueItem[] {
  
      throw new Error("CSV Files are not handled");
  
      // const results = Papa.parse(csvString, { header: true, dynamicTyping: true });
      // if (results.errors.length > 0) {
      //   throw new Error(results.errors.map((e) => e.message).join(', '));
      // }
      // return results.data.map((row: any) => ({
      //   id: row.id,
      //   index: row.index,
      //   title: row.title,
      //   link: row.link,
      //   count: row.count,
      //   start_at: row.start_at,
      //   end_at: row.end_at,
      //   description: row.description,
      //   stacktrace: row.stacktrace,
      //   possible_solution: row.possible_solution,
      //   status: row.status,
      //   createdAt: row.createdAt,
      //   updatedAt: row.updatedAt,
      // }));
  };
  

  /**
   * Fetches a single issue by ID from a local JSON file.
   *
   * @param {number} issueId - The ID of the issue to fetch.
   * @returns {Promise<IssueItem | undefined>} A promise that resolves to the IssueItem object or undefined if not found.
   */
  async getIssueById(issueId: number): Promise<IssueItem | undefined> {
    try {
      // Simulate an API call delay (optional)
      await new Promise((resolve) => setTimeout(resolve, 500)); // 500ms delay

      // Load data from the imported JSON file
      const issues: IssueItem[] = sampleIssues.issues;

      // Find the issue with the matching ID
      const issue = issues.find((i) => i.id === issueId);

      return issue;
    } catch (error) {
      console.error(`Error fetching issue with ID ${issueId}:`, error);
      throw error; // Re-throw the error to be handled by the caller
    }
  }

  /**
   * Fetches issue detail from a local JSON file.
   *
   * @returns {Promise<IssuePerDayCount[]>} A promise that resolves to an array of IssueDetail objects.
   */
  async getIssuePerDayCounts( issueID: string): Promise<IssuePerDayCount[]> {

    console.log( `Load IssuePerDayCounts for ${issueID}`);
    try {
      // Simulate an API call delay (optional)
      await new Promise((resolve) => setTimeout(resolve, 500)); // 500ms delay

      // Load data from the imported JSON file
      const issuePerDayCount: IssuePerDayCount[] = samplePerDateCounts.per_day_counts;

      // Basic validation to ensure data is in the expected format
      if (!Array.isArray(issuePerDayCount)) {
        throw new Error('Invalid data format: Expected an array of issues.');
      }

      issuePerDayCount.forEach(item => {
        item.date = new Date(item.date).toLocaleDateString();
      });

      // Add any necessary data transformations here if needed
      // Example:
      // const transformedIssues = issues.map(issue => ({
      //   ...issue,
      //   createdAt: new Date(issue.createdAt),
      // }));

      return issuePerDayCount;
    } catch (error) {
      console.error('Error fetching issues:', error);
      throw error; // Re-throw the error to be handled by the caller
    }
  }

}

// Create a singleton instance of the service
const issueService = new IssueService();

export default issueService;
